import { Platform } from 'react-native';

import { recordError } from './analytics';

// Lazy-loaded ads module — same pattern as components/AdBanner.tsx.
// babel-preset-expo inlines Platform.OS at build time, so on web this
// `if` becomes `if (false)` and the require() is removed by dead-code
// elimination before Metro can bundle the package. A dynamic
// `await import()` inside an async function would NOT get this treatment
// — Metro bundles dynamic imports statically, which on web pulls
// react-native-google-mobile-ads into the bundle and breaks.
type MobileAdsFactory = () => { initialize: () => Promise<unknown> };
type AdsConsentApi = { gatherConsent: () => Promise<unknown> };

let mobileAds: MobileAdsFactory | null = null;
let adsConsent: AdsConsentApi | null = null;

if (Platform.OS !== 'web') {
  try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const m = require('react-native-google-mobile-ads');
    mobileAds = m.default;
    adsConsent = m.AdsConsent;
  } catch (err) {
    if (__DEV__) console.warn('[ads] module unavailable:', err);
  }
}

let initialized = false;
let initializing: Promise<void> | null = null;

// Initializes the Google Mobile Ads SDK and runs the UMP consent flow.
// No-ops on web and when the native module isn't available. Idempotent —
// safe to call multiple times; in-flight calls are deduplicated via the
// initializing promise.
//
// UMP and SDK init are independent: a misconfigured consent form (common
// while AdMob's "Privacy & messaging" is still empty) must not prevent
// the SDK from initializing — without init, no ads load at all. On
// consent failure we still init the SDK; it will serve non-personalized
// ads where required by the user's region.
export const initializeAds = async (): Promise<void> => {
  if (!mobileAds || !adsConsent || initialized) return;
  if (initializing) return initializing;

  const ads = mobileAds;
  const consent = adsConsent;

  initializing = (async () => {
    try {
      try {
        await consent.gatherConsent();
      } catch (err) {
        recordError(
          err instanceof Error ? err : new Error(String(err)),
          'ads:consent'
        );
      }

      await ads().initialize();
      initialized = true;
    } catch (err) {
      recordError(err instanceof Error ? err : new Error(String(err)), 'ads:init');
    } finally {
      initializing = null;
    }
  })();

  return initializing;
};

export const isAdsInitialized = () => initialized;

export default { initializeAds, isAdsInitialized };
